import { Component, inject, OnInit, effect, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLATION_SCOPE, translationProvider } from './i18n/translation.provider';
import { TranslocoDirective } from '@ngneat/transloco';
import { MandatoryDirectiveModule, PhoneNumberInputComponent, RxAlertModule, RxButtonModule, RxInputModule } from '@roomex/rx-components';
import { AuthConfigService, AuthFacade } from '@rx-applications/shared/auth/data-access';
import {
  ContactInstructionsComponent,
  PasswordMatchFormComponent,
  PasswordPolicyComponent,
} from '@rx-applications/shared/auth/ui';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormUtils } from '@rx-applications/shared/utils-forms';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'rx-applications-invitation',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoDirective,
    RxAlertModule,
    RxInputModule,
    RxButtonModule,
    PasswordMatchFormComponent,
    PasswordPolicyComponent,
    ReactiveFormsModule,
    ContactInstructionsComponent,
    PhoneNumberInputComponent,
    MandatoryDirectiveModule,
  ],
  templateUrl: './invitation.component.html',
  styleUrl: './invitation.component.scss',
  providers: [translationProvider],
})
export class InvitationComponent extends FormUtils implements OnInit {
  readonly TRANSLATION_SCOPE = TRANSLATION_SCOPE;
  readonly authFacade: AuthFacade = inject(AuthFacade);
  readonly isSsoEnabled: boolean = inject(AuthConfigService)?.config.sso.ssoEnabled;
  readonly fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  readonly invitationName = toSignal(this.authFacade.invitationName$, { initialValue: '' });
  readonly invitationPhoneNumber = toSignal(this.authFacade.invitationPhoneNumber$, { initialValue: '' });
  @HostBinding('class.invitation-form') invitationForm = true;

  form!: UntypedFormGroup;
  internationalPhoneNumber: string | null = null;

  constructor() {
    super();
    effect(() => {
      if(this.invitationPhoneNumber()){
        const phoneNumber = this.invitationPhoneNumber();
        this.form.get('phoneNumber')?.setValue(phoneNumber);
        this.internationalPhoneNumber = phoneNumber;
      }
      if(this.invitationName()){
        this.form.get('name')?.setValue(this.invitationName());
      }
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/[\d\s-+()]{6,}/)]],
      name: ['', [Validators.required]],
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      return;
    }

    this.authFacade.acceptInvitation({
      ...this.form.value,
      phoneNumber: this.internationalPhoneNumber,
      name: this.form.get('name')?.value,
    });
  }

  get phoneNumberControl(): FormControl {
    return this.form.get('phoneNumber') as FormControl<string>;
  }

  onPhoneChange(value: string): void {
    this.internationalPhoneNumber = value;
  }
}
